(() => {
  'use strict';
  const selector = '[data-uf-home-reviews]';
  if (window.ufHomeReviews) {
    window.ufHomeReviews.mount(document);
    return;
  }
  const instances = new WeakMap();

  function init(root) {
    if (instances.has(root)) return;
    const slides = Array.from(root.querySelectorAll('[data-review-slide]'));
    if (!slides.length) return;
    const controller = new AbortController();
    const { signal } = controller;
    const viewport = root.querySelector('[data-review-viewport]');
    const rotation = root.querySelector('[data-review-rotation]');
    const controls = root.querySelector('[data-review-controls]');
    const current = root.querySelector('[data-review-current]');
    const status = root.querySelector('[data-review-status]');
    const markers = Array.from(root.querySelectorAll('.uf-reviews__markers i'));
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const configured = root.dataset.autoplay !== 'false';
    const interval = Math.max(6, Number(root.dataset.interval) || 10) * 1000;
    let index = 0;
    let timer = null;
    let hovered = false;
    let ignoreHover = false;
    let inView = false;
    let userPaused = !configured || motion.matches;
    let rotationIntent = null;
    let gesture = null;
    let observer = null;

    function clearTimer() {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      root.dataset.playing = 'false';
    }

    function updateRotation() {
      rotation.dataset.userPaused = String(userPaused);
      rotation.setAttribute('aria-label', userPaused ? 'Start automatic reviews' : 'Pause automatic reviews');
      rotation.title = userPaused ? 'Start automatic reviews' : 'Pause automatic reviews';
    }

    function schedule() {
      clearTimer();
      const allowed = slides.length > 1 && !userPaused && inView && !document.hidden && (!hovered || ignoreHover);
      viewport.setAttribute('aria-live', allowed ? 'off' : 'polite');
      if (!allowed) return;
      const words = slides[index].querySelector('blockquote').textContent.trim().split(/\s+/).length;
      const delay = Math.max(interval, words * 320 + 2000);
      root.dataset.playing = 'true';
      timer = window.setTimeout(() => {
        timer = null;
        show(index + 1, 1, false);
      }, delay);
    }

    function pause() {
      userPaused = true;
      ignoreHover = false;
      updateRotation();
      schedule();
    }

    function show(nextIndex, direction = 1, manual = true) {
      const previous = index;
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, n) => {
        const active = n === index;
        slide.style.setProperty('--uf-review-offset', ((n === previous ? -direction : direction) * 36) + 'px');
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        const source = slide.querySelector('a');
        if (source) source.tabIndex = active ? 0 : -1;
      });
      markers.forEach((marker, n) => marker.classList.toggle('is-active', n === index));
      current.textContent = String(index + 1).padStart(2, '0');
      if (manual) status.textContent = 'Review ' + (index + 1) + ' of ' + slides.length + ': ' + slides[index].querySelector('strong').textContent;
      schedule();
    }

    function manual(nextIndex, direction) {
      pause();
      show(nextIndex, direction, true);
    }

    rotation.addEventListener('pointerdown', () => { rotationIntent = !userPaused; }, { signal });
    rotation.addEventListener('pointercancel', () => { rotationIntent = null; }, { signal });
    rotation.addEventListener('click', () => {
      userPaused = rotationIntent === null ? !userPaused : rotationIntent;
      rotationIntent = null;
      ignoreHover = !userPaused;
      updateRotation();
      schedule();
    }, { signal });
    root.querySelector('[data-review-prev]').addEventListener('click', () => manual(index - 1, -1), { signal });
    root.querySelector('[data-review-next]').addEventListener('click', () => manual(index + 1, 1), { signal });

    root.addEventListener('pointerenter', event => {
      if (event.pointerType === 'touch') return;
      hovered = true;
      ignoreHover = false;
      schedule();
    }, { signal });
    root.addEventListener('pointerleave', event => {
      if (event.pointerType === 'touch') return;
      hovered = false;
      ignoreHover = false;
      schedule();
    }, { signal });
    root.addEventListener('focusin', pause, { signal });
    root.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      manual(index + (event.key === 'ArrowRight' ? 1 : -1), event.key === 'ArrowRight' ? 1 : -1);
    }, { signal });
    viewport.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
      gesture = { x: event.clientX, y: event.clientY, id: event.pointerId };
      pause();
    }, { signal, passive: true });
    viewport.addEventListener('pointerup', event => {
      if (!gesture || gesture.id !== event.pointerId) return;
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      gesture = null;
      if (Math.abs(dx) < 42 || Math.abs(dx) < Math.abs(dy) * 1.25) return;
      manual(index + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
    }, { signal, passive: true });
    viewport.addEventListener('pointercancel', () => { gesture = null; }, { signal });

    document.addEventListener('visibilitychange', schedule, { signal });
    window.addEventListener('pagehide', clearTimer, { signal });
    window.addEventListener('pageshow', schedule, { signal });
    const onMotionChange = () => { if (motion.matches) pause(); };
    if (motion.addEventListener) motion.addEventListener('change', onMotionChange);
    else motion.addListener(onMotionChange);

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        inView = entries[0].isIntersecting && entries[0].intersectionRatio >= .5;
        schedule();
      }, { threshold: [0, .5] });
      observer.observe(viewport);
    } else {
      const checkView = () => {
        const rect = viewport.getBoundingClientRect();
        inView = rect.bottom > 0 && rect.top < window.innerHeight;
        schedule();
      };
      window.addEventListener('scroll', checkView, { signal, passive: true });
      window.addEventListener('resize', checkView, { signal, passive: true });
      checkView();
    }

    document.addEventListener('shopify:block:select', event => {
      const selected = slides.findIndex(slide => slide.dataset.reviewBlock === event.detail?.blockId);
      if (selected !== -1) manual(selected, selected >= index ? 1 : -1);
    }, { signal });

    rotation.hidden = slides.length < 2;
    controls.hidden = slides.length < 2;
    root.classList.add('is-ready');
    updateRotation();
    show(0, 1, false);
    instances.set(root, () => {
      clearTimer();
      controller.abort();
      observer?.disconnect();
      if (motion.removeEventListener) motion.removeEventListener('change', onMotionChange);
      else motion.removeListener(onMotionChange);
      root.classList.remove('is-ready');
      slides.forEach(slide => {
        slide.classList.remove('is-active');
        slide.removeAttribute('aria-hidden');
        slide.inert = false;
        slide.querySelector('a')?.removeAttribute('tabindex');
      });
      rotation.hidden = true;
      controls.hidden = true;
      instances.delete(root);
    });
  }

  function mount(scope) {
    if (scope.matches?.(selector)) init(scope);
    scope.querySelectorAll(selector).forEach(init);
  }
  function unmount(scope) {
    if (scope.matches?.(selector)) instances.get(scope)?.();
    scope.querySelectorAll(selector).forEach(root => instances.get(root)?.());
  }

  window.ufHomeReviews = { mount };
  mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
})();
